import * as React from "react";
import { useLogin, useNotify } from "react-admin";
import {
  Box,
  TextField,
  Button,
  Typography,
  CircularProgress,
  Alert,
  InputAdornment,
  IconButton,
  Stack,
} from "@mui/material";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";

// Connexion back-office : email + mot de passe → authProvider.login
// (POST /admin/auth/login). Logique inchangée, seul l'habillage évolue.
export const LoginPage = () => {
  const [email, setEmail] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [showPwd, setShowPwd] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(null);
  const login = useLogin();
  const notify = useNotify();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await login({ username: email, password });
    } catch (err) {
      setError(err?.message || "Identifiants invalides");
      notify("Échec de connexion", { type: "error" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "grid",
        gridTemplateColumns: { xs: "1fr", md: "1.05fr 1fr" },
        bgcolor: "#F5F7F5",
      }}
    >
      {/* Panneau de marque */}
      <Box
        sx={{
          display: { xs: "none", md: "flex" },
          flexDirection: "column",
          justifyContent: "space-between",
          p: 6,
          color: "#fff",
          background:
            "radial-gradient(120% 90% at 0% 0%, rgba(74,222,128,0.22) 0%, rgba(74,222,128,0) 55%), linear-gradient(150deg, #0D2B1A 0%, #145C37 55%, #1B8550 100%)",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box sx={{ width: 40, height: 40, borderRadius: 3, bgcolor: "rgba(255,255,255,0.14)", display: "grid", placeItems: "center", fontFamily: "Sora, sans-serif", fontWeight: 700 }}>
            io
          </Box>
          <Typography sx={{ fontFamily: "Sora, sans-serif", fontWeight: 700, fontSize: 20 }}>ioUman Admin</Typography>
        </Stack>
        <Box sx={{ maxWidth: 440 }}>
          <Typography sx={{ fontFamily: "Sora, sans-serif", fontWeight: 700, fontSize: 34, lineHeight: 1.2, letterSpacing: "-0.02em" }}>
            Pilotez la plateforme ioUman en toute sérénité.
          </Typography>
          <Typography sx={{ mt: 2, opacity: 0.8, fontSize: 16 }}>
            Patients, professionnels, rendez-vous, paiements et contenus : tout au même endroit.
          </Typography>
        </Box>
        <Typography sx={{ opacity: 0.6, fontSize: 13 }}>© ioUman — accès réservé à l'équipe</Typography>
        <Box
          aria-hidden
          sx={{ position: "absolute", right: -120, bottom: -120, width: 360, height: 360, borderRadius: "50%", border: "1px solid rgba(255,255,255,0.12)" }}
        />
        <Box
          aria-hidden
          sx={{ position: "absolute", right: -60, bottom: -60, width: 240, height: 240, borderRadius: "50%", border: "1px solid rgba(255,255,255,0.12)" }}
        />
      </Box>

      {/* Formulaire */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", p: { xs: 2, sm: 4 } }}>
        <Box
          sx={{
            width: "100%",
            maxWidth: 400,
            bgcolor: "#fff",
            borderRadius: 4,
            border: "1px solid #E8EEE8",
            boxShadow: "0 12px 40px rgba(13,26,13,0.06)",
            p: { xs: 3, sm: 4 },
          }}
        >
          <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 3, display: { md: "none" } }}>
            <Box sx={{ width: 34, height: 34, borderRadius: 2.5, bgcolor: "#1B8550", color: "#fff", display: "grid", placeItems: "center", fontFamily: "Sora, sans-serif", fontWeight: 700 }}>
              io
            </Box>
            <Typography sx={{ fontFamily: "Sora, sans-serif", fontWeight: 700, fontSize: 18 }}>ioUman Admin</Typography>
          </Stack>
          <Typography variant="h5" sx={{ color: "#0D1A0D" }}>
            Connexion
          </Typography>
          <Typography variant="body2" sx={{ color: "#6B7280", mt: 0.5, mb: 3 }}>
            Identifiez-vous pour accéder au back-office.
          </Typography>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <form onSubmit={handleSubmit} noValidate={false}>
            <Stack spacing={2}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoFocus
                required
                autoComplete="username"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <MailOutlineIcon fontSize="small" sx={{ color: "#9CA3AF" }} />
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                fullWidth
                label="Mot de passe"
                type={showPwd ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockOutlinedIcon fontSize="small" sx={{ color: "#9CA3AF" }} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label={showPwd ? "Masquer le mot de passe" : "Afficher le mot de passe"}
                        onClick={() => setShowPwd((v) => !v)}
                        edge="end"
                        size="small"
                      >
                        {showPwd ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={loading}
                sx={{ mt: 1, py: 1.3, fontSize: 15, borderRadius: 2.5 }}
              >
                {loading ? <CircularProgress size={22} color="inherit" /> : "Se connecter"}
              </Button>
            </Stack>
          </form>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 3, color: "#6B7280" }}>
            <ShieldOutlinedIcon sx={{ fontSize: 16 }} />
            <Typography variant="caption">Session sécurisée de 8 h · accès administrateur uniquement</Typography>
          </Stack>
        </Box>
      </Box>
    </Box>
  );
};

export default LoginPage;
